import { AbsoluteFill, interpolate, useCurrentFrame } from "remotion";
import {
  Caption,
  Callout,
  CodePanel,
  COLORS,
  FONTS,
  Panel,
  SceneTransition,
  SlideTimeBar,
} from "../../coldcompute";
import { SCENE } from "./constants";
import { HOOK, TRIAGE, PROBE, DOCKERPS, PROOF, ROOTCAUSE, FIX, PREVENT, OUTRO } from "./script";

const center: React.CSSProperties = {
  alignItems: "center",
  justifyContent: "center",
  flexDirection: "column",
};

const Kicker: React.FC<{ text: string; color: string }> = ({ text, color }) => (
  <div
    style={{
      fontFamily: FONTS.mono,
      fontSize: 22,
      letterSpacing: "0.22em",
      textTransform: "uppercase",
      color,
      marginBottom: 22,
    }}
  >
    ▍{text}
  </div>
);

const Big: React.FC<{ children: React.ReactNode; size?: number }> = ({ children, size = 92 }) => (
  <div
    style={{
      fontFamily: FONTS.display,
      fontWeight: 700,
      fontSize: size,
      lineHeight: 0.98,
      letterSpacing: "-0.02em",
      color: COLORS.text,
      textAlign: "center",
      maxWidth: 1560,
    }}
  >
    {children}
  </div>
);

const Stat: React.FC<{ label: string; value: string; ok?: boolean }> = ({ label, value, ok = true }) => (
  <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 6, minWidth: 150 }}>
    <div style={{ fontFamily: FONTS.display, fontWeight: 700, fontSize: 46, color: ok ? COLORS.green : COLORS.red }}>{value}</div>
    <div style={{ fontFamily: FONTS.mono, fontSize: 15, color: COLORS.dim, letterSpacing: "0.08em" }}>{label}</div>
  </div>
);

/* 01 — HOOK */
export const Scene01Hook: React.FC = () => {
  const frame = useCurrentFrame();
  const flash = interpolate(frame, [40, 70], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  return (
    <AbsoluteFill style={center}>
      <SlideTimeBar sceneDurationFrames={SCENE.hook.duration} />
      <SceneTransition from="up">
        <AbsoluteFill style={center}>
          <Kicker text="incident · total outage" color={COLORS.red} />
          <Big>
            every site went{" "}
            <span style={{ color: COLORS.red, opacity: 0.4 + flash * 0.6 }}>down</span>
            <br />
            the server was <span style={{ color: COLORS.green }}>fine</span>
          </Big>
        </AbsoluteFill>
      </SceneTransition>
      <Caption lines={HOOK} accent="red" />
    </AbsoluteFill>
  );
};

/* 02 — TRIAGE (the box is healthy) */
export const Scene02Triage: React.FC = () => (
  <AbsoluteFill style={center}>
    <SlideTimeBar sceneDurationFrames={SCENE.triage.duration} />
    <SceneTransition from="up">
      <AbsoluteFill style={center}>
        <Kicker text="step 1 · is the box dead?" color={COLORS.cyan} />
        <Panel accent="green" title="hostinger vps · overview" width={1180}>
          <div style={{ display: "flex", justifyContent: "space-around", alignItems: "center", padding: "22px 8px" }}>
            <Stat label="status" value="RUNNING" />
            <Stat label="cpu" value="17%" />
            <Stat label="memory" value="46%" />
            <Stat label="disk" value="27%" />
            <Stat label="uptime" value="38m" ok={false} />
          </div>
        </Panel>
        <div style={{ fontFamily: FONTS.body, fontSize: 26, color: COLORS.dim, marginTop: 26 }}>
          healthy box — but it had <span style={{ color: COLORS.amber }}>just rebooted</span>
        </div>
      </AbsoluteFill>
    </SceneTransition>
    <Caption lines={TRIAGE} accent="cyan" />
  </AbsoluteFill>
);

/* 03 — PROBE (TCP open, HTTP hangs) */
export const Scene03Probe: React.FC = () => (
  <AbsoluteFill style={center}>
    <SlideTimeBar sceneDurationFrames={SCENE.probe.duration} />
    <SceneTransition from="up">
      <AbsoluteFill style={center}>
        <Kicker text="step 2 · probe from outside" color={COLORS.cyan} />
        <CodePanel
          filename="probe.sh — from a remote shell"
          language="bash"
          width={1180}
          fontSize={25}
          highlightLines={[2, 4]}
          code={`# TCP handshake to the web ports
443 OPEN        80 OPEN          # the ports accept connections...

curl -I https://my-site …       # ...but HTTP never answers
  ↳ timed out after 12s — 0 bytes received`}
        />
        <div style={{ fontFamily: FONTS.body, fontSize: 26, color: COLORS.dim, marginTop: 24 }}>
          connect succeeds, HTTP hangs → the <span style={{ color: COLORS.amber }}>proxy is wedged</span>, not the network
        </div>
      </AbsoluteFill>
    </SceneTransition>
    <Caption lines={PROBE} accent="amber" />
  </AbsoluteFill>
);

/* 04 — DOCKER PS (apps Up, traefik missing) */
export const Scene04DockerPs: React.FC = () => {
  const frame = useCurrentFrame();
  const reveal = interpolate(frame, [120, 160], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  const apps = ["eakc", "n8n", "jyotishai", "idea-radar", "remotion", "lmt", "dokploy", "postgres", "redis"];
  return (
    <AbsoluteFill style={center}>
      <SlideTimeBar sceneDurationFrames={SCENE.dockerps.duration} />
      <SceneTransition from="up">
        <AbsoluteFill style={center}>
          <Kicker text="step 3 · look inside" color={COLORS.cyan} />
          <Panel accent="cyan" title="docker ps" width={1180}>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 10, padding: 10, justifyContent: "center" }}>
              {apps.map((a) => (
                <div
                  key={a}
                  style={{
                    fontFamily: FONTS.mono,
                    fontSize: 19,
                    color: COLORS.green,
                    border: `1px solid ${COLORS.line}`,
                    borderRadius: 6,
                    padding: "8px 14px",
                  }}
                >
                  ● {a} <span style={{ color: COLORS.dim }}>Up</span>
                </div>
              ))}
              <div
                style={{
                  fontFamily: FONTS.mono,
                  fontSize: 19,
                  color: COLORS.red,
                  border: `1px dashed ${COLORS.red}`,
                  borderRadius: 6,
                  padding: "8px 14px",
                  opacity: 0.35 + reveal * 0.65,
                }}
              >
                ✕ dokploy-traefik <span style={{ color: COLORS.red }}>MISSING</span>
              </div>
            </div>
          </Panel>
          <div style={{ fontFamily: FONTS.body, fontSize: 26, color: COLORS.dim, marginTop: 24, opacity: reveal }}>
            the one container that routes <span style={{ color: COLORS.text }}>every</span> site — gone
          </div>
        </AbsoluteFill>
      </SceneTransition>
      <Caption lines={DOCKERPS} accent="red" />
    </AbsoluteFill>
  );
};

/* 05 — PROOF (nothing on 80/443) */
export const Scene05Proof: React.FC = () => (
  <AbsoluteFill style={center}>
    <SlideTimeBar sceneDurationFrames={SCENE.proof.duration} />
    <SceneTransition from="up">
      <AbsoluteFill style={center}>
        <Kicker text="step 4 · the definitive proof" color={COLORS.cyan} />
        <CodePanel
          filename="confirm.sh"
          language="bash"
          width={1100}
          fontSize={26}
          highlightLines={[2]}
          code={`$ ss -tlnp | grep -E ':80|:443'
(nothing)   # NOTHING is listening on 80 or 443`}
        />
        <Big size={60}>
          no router = <span style={{ color: COLORS.red }}>no door</span>
        </Big>
      </AbsoluteFill>
    </SceneTransition>
    <Caption lines={PROOF} accent="red" />
  </AbsoluteFill>
);

/* 06 — ROOT CAUSE (the asymmetry) */
export const Scene06RootCause: React.FC = () => {
  const rows: Array<{ k: string; v: string; accent: "green" | "red" | "amber" }> = [
    { k: "your apps", v: "Swarm services → auto-recreated on boot", accent: "green" },
    { k: "traefik", v: "plain container → not Swarm-managed, not reboot-safe", accent: "red" },
    { k: "dokploy", v: "crash-looped on boot (exit 255 ×2) → never recreated traefik", accent: "amber" },
  ];
  return (
    <AbsoluteFill style={center}>
      <SlideTimeBar sceneDurationFrames={SCENE.rootcause.duration} />
      <SceneTransition from="up">
        <AbsoluteFill style={center}>
          <Kicker text="root cause · the asymmetry" color={COLORS.amber} />
          <div style={{ display: "flex", flexDirection: "column", gap: 16, width: 1180 }}>
            {rows.map((r) => (
              <div
                key={r.k}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 22,
                  border: `1px solid ${COLORS.line}`,
                  borderLeft: `4px solid ${COLORS[r.accent]}`,
                  borderRadius: 8,
                  background: COLORS.panel,
                  padding: "18px 24px",
                }}
              >
                <div style={{ fontFamily: FONTS.mono, fontSize: 22, color: COLORS[r.accent], minWidth: 150, letterSpacing: "0.06em" }}>{r.k}</div>
                <div style={{ fontFamily: FONTS.body, fontSize: 25, color: COLORS.text }}>{r.v}</div>
              </div>
            ))}
          </div>
        </AbsoluteFill>
      </SceneTransition>
      <Caption lines={ROOTCAUSE} accent="amber" />
    </AbsoluteFill>
  );
};

/* 07 — THE FIX (recreate with --restart always) */
export const Scene07Fix: React.FC = () => (
  <AbsoluteFill style={center}>
    <SlideTimeBar sceneDurationFrames={SCENE.fix.duration} />
    <SceneTransition from="up">
      <AbsoluteFill style={center}>
        <Kicker text="the fix · recreate, reboot-safe" color={COLORS.green} />
        <CodePanel
          filename="restore-traefik.sh"
          language="bash"
          width={1240}
          fontSize={22}
          highlightLines={[2, 8]}
          code={`docker run -d --name dokploy-traefik \\
  --restart always \\
  -v /etc/dokploy/traefik/traefik.yml:/etc/dokploy/traefik/traefik.yml \\
  -v /etc/dokploy/traefik/dynamic:/etc/dokploy/traefik/dynamic \\
  -v /var/run/docker.sock:/var/run/docker.sock \\
  -p 80:80 -p 443:443 -p 443:443/udp \\
  --network dokploy-network \\
  traefik:v3.1.2 --configFile=/etc/dokploy/traefik/traefik.yml`}
        />
        <Callout label="back in under a minute" accent="green" width={620} style={{ marginTop: 22 }}>
          <span style={{ fontFamily: FONTS.body, fontSize: 22, color: COLORS.text }}>
            Up on 80/443 · certs from the intact <span style={{ color: COLORS.cyan }}>acme.json</span> · no data lost
          </span>
        </Callout>
      </AbsoluteFill>
    </SceneTransition>
    <Caption lines={FIX} accent="green" />
  </AbsoluteFill>
);

/* 08 — PREVENT (the three guards) */
export const Scene08Prevent: React.FC = () => {
  const guards = [
    { n: "1", t: "--restart always", d: "on Traefik — survives any future reboot", accent: "green" as const },
    { n: "2", t: "add swap", d: "0 swap on 8 GB + ~16 containers was a cliff edge", accent: "cyan" as const },
    { n: "3", t: "uptime monitor", d: "get paged in minutes, not 'when you notice'", accent: "violet" as const },
  ];
  return (
    <AbsoluteFill style={center}>
      <SlideTimeBar sceneDurationFrames={SCENE.prevent.duration} />
      <SceneTransition from="up">
        <AbsoluteFill style={center}>
          <Kicker text="so it can't recur" color={COLORS.green} />
          <div style={{ display: "flex", gap: 22 }}>
            {guards.map((g) => (
              <Panel key={g.n} accent={g.accent} width={380}>
                <div style={{ display: "flex", flexDirection: "column", gap: 10, padding: 6, minHeight: 150 }}>
                  <div style={{ fontFamily: FONTS.display, fontWeight: 700, fontSize: 40, color: COLORS[g.accent] }}>{g.n}</div>
                  <div style={{ fontFamily: FONTS.mono, fontSize: 22, color: COLORS.text }}>{g.t}</div>
                  <div style={{ fontFamily: FONTS.body, fontSize: 19, color: COLORS.dim }}>{g.d}</div>
                </div>
              </Panel>
            ))}
          </div>
        </AbsoluteFill>
      </SceneTransition>
      <Caption lines={PREVENT} accent="green" />
    </AbsoluteFill>
  );
};

/* 09 — OUTRO (the lesson) */
export const Scene09Outro: React.FC = () => (
  <AbsoluteFill style={center}>
    <SlideTimeBar sceneDurationFrames={SCENE.outro.duration} />
    <SceneTransition from="up">
      <AbsoluteFill style={center}>
        <Kicker text="the lesson" color={COLORS.cyan} />
        <Big size={70}>
          a reboot's blast radius is decided
          <br />
          <span style={{ color: COLORS.cyan }}>before</span> the reboot
        </Big>
        <div style={{ fontFamily: FONTS.mono, fontSize: 24, color: COLORS.dim, marginTop: 28 }}>
          your proxy is a single point of failure — make it <span style={{ color: COLORS.green }}>restart: always</span>
        </div>
      </AbsoluteFill>
    </SceneTransition>
    <Caption lines={OUTRO} accent="cyan" />
  </AbsoluteFill>
);

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
